import { useState, useEffect } from 'react';
import { supabase } from '@/integrations/supabase/client';
import { useToast } from '@/hooks/use-toast';

interface MatchingListing {
  id: string;
  title: string;
  price_usd: number | null;
  year_manufactured: number | null;
  total_flight_hours: number | null;
  condition: string | null;
  location: string | null;
  images: string[] | null;
  helicopters: {
    manufacturer: string;
    model: string;
    icon_url: string | null;
  };
}

export const useMatchingListings = (
  requestHelicopterId: string | null,
  requestListingType: string,
  userId: string | null
) => {
  const [matchingListings, setMatchingListings] = useState<MatchingListing[]>([]);
  const [loading, setLoading] = useState(false);
  const { toast } = useToast();

  useEffect(() => {
    if (requestHelicopterId && userId) {
      fetchMatchingListings();
    }
  }, [requestHelicopterId, requestListingType, userId]);

  const fetchMatchingListings = async () => {
    if (!requestHelicopterId || !userId) return;

    try {
      setLoading(true);

      // Determine the matching listing type based on request type
      const matchingType = (() => {
        if (requestListingType === 'buy_sale') return 'sale';
        if (requestListingType === 'buy_dry_lease') return 'dry_lease';
        if (requestListingType === 'buy_wet_lease') return 'wet_lease';
        return 'sale';
      })();

      const { data, error } = await supabase
        .from('helicopter_listings')
        .select(`
          id,
          title,
          price_usd,
          year_manufactured,
          total_flight_hours,
          condition,
          location,
          images,
          helicopters (
            manufacturer,
            model,
            icon_url
          )
        `)
        .eq('user_id', userId)
        .eq('helicopter_id', requestHelicopterId)
        .eq('listing_type', matchingType)
        .eq('status', 'active')
        .eq('payment_status', 'paid')
        .order('created_at', { ascending: false })
        .limit(10);

      if (error) throw error;

      setMatchingListings((data as any) || []);
    } catch (error) {
      console.error('Error fetching matching listings:', error);
      toast({
        title: "Error loading listings",
        description: "Failed to load your matching listings.",
        variant: "destructive",
      });
    } finally {
      setLoading(false);
    }
  };

  return { matchingListings, loading, refetch: fetchMatchingListings };
};
